// import { EMAIL_VALIDATE_REGEX } from '@/configs/constants';
import CoppyIcon from '@/assets/icons/CoppyIcon';
import EyeNewIcon from '@/assets/icons/EyeIcon';
import TrashIcon from '@/assets/icons/TrashIcon';
import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { convertDate, convertFileSizeToMb } from '@/utils/utils';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { MapPin, Plus } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
const FILE_SIZE = 10 * 1024 * 1024;

export default observer(function LibraryManagementPage() {
    // hooks
    const { t } = useTranslation();
    // const navigate = useNavigate();

    // store
    const {
        libraryStore: { getAllFile, clean, deleteFile, uploadFile, allFile, totalRecord, paging },
        modalStore: { showAlertModal }
    } = useStore();

    // state
    const [rowSelected, setRowSelected] = useState({});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const form = useForm();
    const columnsItem: ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'No.',
            size: 50,
            enableSorting: false,
            cell: ({ row }) => {
                const { pageIndex, pageSize } = paging.pagination;
                const index = (row.index + 1) + (pageIndex * pageSize);
                return index;
            }
        },
        {
            accessorKey: 'path',
            header: t('words_title.thumbnail'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <>
                        <img className='size-10 rounded-[4px]' src={original.path} alt='' />
                    </>
                );
            }

        },
        {
            accessorKey: 'filename',
            header: t('words_title.file_name'),
            size: 600,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <div className=''>{original.filename}</div>
                );
            }
        },
        {
            accessorKey: 'size',
            header: t('words_title.file_size'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <span>
                        {convertFileSizeToMb(original.size, 3)}
                        {' '}
                        MB
                    </span>
                );
            }
        },
        {
            accessorKey: 'filetype',
            header: t('words_title.file_format'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                return (
                    <span>
                        {original.filetype.replace(/^image\//, '').toUpperCase()}
                    </span>
                );
            }
        },
        {
            // 画像 EXIF GPS (2026-07-23)。位置情報付き写真のみ座標を表示。
            // 一括登録 CSV で座標空欄なら、この座標が使われる。
            accessorKey: 'latitude',
            header: t('words_title.geo_location'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                if (original?.latitude == null || original?.longitude == null) {
                    return <span className='text-text-gray-1 text-[13px]'>{t('words_title.no_geo')}</span>;
                }
                const coord = `${Number(original.latitude).toFixed(5)}, ${Number(original.longitude).toFixed(5)}`;
                return (
                    <span className='inline-flex items-center gap-1 text-[13px] font-mono text-active'>
                        <MapPin size={14} />
                        {coord}
                    </span>
                );
            }
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.upload_date'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className=''>
                    <div>
                        {convertDate(original?.createdAt)?.date}
                    </div>
                    <div>
                        {convertDate(original?.createdAt)?.time}
                        <span className='text-timezone ml-1'>{convertDate(original?.createdAt)?.ampm}</span>
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'action',
            header: t('words_title.action_2'),
            size: 400,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex space-x-2'>
                    <Button
                        onClick={() => handleViewFile(original)}
                        title={t('field.active')}
                        size='icon'
                        variant='ghost'
                        className='rounded-full bg-background-view'
                    >
                        <EyeNewIcon />
                    </Button>
                    <Button
                        onClick={() => handleCopyLink(original)}
                        title={t('field.active')}
                        size='icon'
                        variant='ghost'
                        className='rounded-full bg-background-active'
                    >
                        <CoppyIcon />
                    </Button>

                </div>
            )
        }
    ];

    // lifecycle
    useEffect(() => {
        fetchData();
        return () => {
            clean();
        };
    }, []);

    // function
    const fetchData = async () => {
        await flowResult(getAllFile(paging));
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getAllFile(newPaging);
    };

    const handleViewFile = (original) => {
        showAlertModal({
            size: 'sm',
            headerClassName: 'border-b border-b-border-7 pb-8',
            contentClassName: 'pb-0 pt-6',
            footerClassName: 'hidden',
            content: <img className='w-full h-[334px] rounded-[4px]' src={original.path} alt='' />
        });
    };

    const handleCopyLink = (original) => {
        const imageUrl = original.path;

        navigator.clipboard.writeText(imageUrl)
            .then(() => {
                toastify('success', t('messages.coppy_success', { url: imageUrl }), {
                    className: 'whitespace-pre-line'
                });
            })
            .catch((err) => {
                toastify('error', 'リンクをコピーできません');
            });
    };

    const handleDeleteItem = async (arrayId) => {
        showAlertModal({
            title: t('words_title.confirm'),
            size: 'sm',
            headerClassName: 'border-b border-b-border-7',
            content: t('sentences.confirm_delete_file'),
            onSave: async () => {
                const res = await flowResult(deleteFile({
                    ids: Object.keys(rowSelected).map(Number)
                }));
                if (res && res?.ok) {
                    toastify('success', t('messages.delete_file_success'));
                    getAllFile(paging);
                    setRowSelected({});
                } else {
                    toastify('error', res?.message);
                }
            }
        });
    };

    const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        let validFiles: File[] = [];
        let invalidFileTypes: string[] = [];
        let invalidFileSizes: string[] = [];
        for (let file of Array.from(files)) {
            const isValidType = SUPPORTED_FORMATS.includes(file.type);
            const isValidSize = file.size <= FILE_SIZE;

            if (!isValidType) {
                invalidFileTypes.push(file.name);
            }

            if (!isValidSize) {
                invalidFileSizes.push(file.name);
            }

            if (isValidType && isValidSize) {
                validFiles.push(file);
            }
        }
        if (invalidFileTypes.length > 0) {
            toastify('error', t('form_error_validate.format_image'));
        }
        if (invalidFileSizes.length > 0) {
            toastify('error', t('form_error_validate.maximum_size_image'));
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        if (invalidFileTypes.length > 0 || invalidFileSizes.length > 0) {
            return;
        }

        if (validFiles.length > 0) {
            form.setValue('file', validFiles);

            const formData = new FormData();
            validFiles.forEach((file) => {
                formData.append('file', file);
            });

            try {
                const res = await flowResult(uploadFile(formData));
                if (res) {
                    getAllFile(paging);
                }
            } catch (error) {
            }
        }
    };

    return (
        <>
            <div className='layout-card'>
                <Table
                    columns={columnsItem}
                    data={allFile}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                    enableRowSelection={row => row?.original.isLidUse === false}
                    onRowSelectionChange={setRowSelected}
                    showButtonAction={(
                        <div className='flex items-center gap-4 mt-4 md:mt-0'>
                            <Button
                                onClick={() => handleDeleteItem(rowSelected)}
                                disabled={Object.keys(rowSelected).length === 0}
                                size='xl'
                                variant='active'
                                className='h-[3.25rem] w-[13.5rem] text-[16px] flex items-center space-x-1.5 bg-red-6'
                            >
                                <TrashIcon color='#FFFFFF' className='size-[22px]' />
                                <p className='text-[16px] font-semibold'>{t('button.delete')}</p>
                            </Button>
                            <Form {...form}>
                                <form>
                                    <FormField
                                        control={form.control}
                                        name='file'
                                        render={({ field }) => (
                                            <div className=''>
                                                <FormItem className='w-full h-full flex-1'>
                                                    <FormControl>
                                                        <>
                                                            <Input
                                                                ref={fileInputRef}
                                                                type='file'
                                                                id='file-input'
                                                                className='hidden h-full w-full'
                                                                accept='image/*'
                                                                multiple={true}
                                                                onChange={onFileChange}
                                                            />

                                                            <Button onClick={() => fileInputRef.current?.click()} size='xl' type='button' variant='active' className='h-[3.25rem] w-[13.5rem] text-[16px] flex items-center space-x-1.5'>
                                                                <Plus className='size-[22px]' />
                                                                <p className='text-[16px] font-semibold'>{t('button.add_new_photo')}</p>
                                                            </Button>

                                                        </>
                                                    </FormControl>
                                                </FormItem>
                                            </div>
                                        )}
                                    />
                                </form>
                            </Form>
                        </div>
                    )}
                />
            </div>
        </>
    );
});
